import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-core';

import { Configure } from '@/components/Configure';
import { Spacer } from '@/components/Spacer';
import { ListingPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import {
  buildQueryParamsFromSearchUiState,
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { Default } from '@/layouts/Default';
import { getRegionFromLocale } from '@/lib/regions';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { listingActions } from '@/states/listingState';
import { usePreRenderCmsSections } from '@/views/StrapMaterialView/usePreRenderCmsSections';

import type { StrapMaterialViewProps } from './types';

export const StrapMaterialView = ({
  algoliaFilters,
  algoliaUserToken,
  cmsData,
  draftMode,
  instantSearchProps,
  pageName,
  serverState,
}: StrapMaterialViewProps) => {
  const liveCmsData = useContentfulLiveUpdates(cmsData, {
    query: ListingPageDocument,
  });
  const { cmsSectionAll } = usePreRenderCmsSections(liveCmsData, pageName);
  const { asPath, locale, push, query } = useRouter();
  const indexName = getIndexName('PRODUCT', locale);
  const lastUiState = useRef<SearchUiState | null>(null);
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_STRAPS,
  });
  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    return buildUiStateFromURL(
      asPath,
      indexName,
      undefined,
      undefined,
      region.locale,
      serverState,
      'EN_US',
    );
  }, [asPath, indexName, locale, serverState]);

  const syncStateWithUrl = useCallback(
    (uiState: SearchUiState) => {
      const queryParams = buildQueryParamsFromSearchUiState(
        uiState,
        indexName,
        query,
        serverState,
        'EN_US',
      );

      // Don't add material to query params - it's already in the URL path
      // This prevents duplicate material=rubber appearing in the query string
      delete queryParams.material;

      const cleanPathname = asPath.split('?')[0];
      push({ pathname: cleanPathname, query: queryParams }, undefined, {
        shallow: true,
      });
    },
    [asPath, indexName, push, query, serverState],
  );

  useEffect(() => {
    listingActions.updateListingType('STRAP');
  }, []);

  // Destructure to remove initialUiState and serverState from instantSearchProps
  // to prevent them from overriding our translated uiStateFromURL and serverState prop
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialUiState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serverState: _serverStateFromProps,
    ...restInstantSearchProps
  } = (instantSearchProps as any) || {};

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName={getIndexName('PRODUCT', locale ?? '')}
        initialUiState={uiStateFromURL}
        insights={false}
        onStateChange={({ setUiState, uiState }) => {
          if (!isEqual(uiState, lastUiState.current)) {
            lastUiState.current = uiState;
            setUiState(uiState);
            syncStateWithUrl(uiState);
          }
        }}
        searchClient={algoliaSearchClient}
        {...restInstantSearchProps}
      >
        <Configure
          analyticsTags={[
            'platform:web',
            'product:straps',
            'page:detail',
            'context:strap-material',
          ]}
          clickAnalytics={true}
          facets={['*']}
          filters={algoliaFilters}
          userToken={algoliaUserToken}
        />
        {cmsSectionAll}
        <Spacer height={32} />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

StrapMaterialView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      draftMode={page.props.draftMode}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
